import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router';
import { wishlistStore, cartStore, authStore } from '@stores/main';
import Product from '@models/product';
import { useToast } from '../../contexts/ToastContext';
import { generateProductDetailsUrl, optimizeImageUrl } from '@utils/urlUtils';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';
import { useBreadcrumbNavigation } from '@utils/breadcrumbNavigation';
import Header from '../HomeRedesign/components/Header/Header';
import Footer from '../HomeRedesign/components/Footer/Footer';
import Loader from '@components/Loader/Loader';
import { ItemMessage } from '@components/ItemMessage';
import { pageTrackingService } from '../../services/pageTracking';
import styles from './WishlistRedesign.module.scss';

const WishlistRedesign: React.FC = observer(() => {
  const viewTrackedRef = React.useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current) return; // StrictMode double-invoke guard
    viewTrackedRef.current = true;
    pageTrackingService.trackWishlistView(wishlistStore.wishlist.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { wishlist, isLoading, error } = wishlistStore;
  const navigate = useNavigate();
  const { goToLogin } = useBreadcrumbNavigation();
  const { showToast } = useToast();
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    // Fetch wishlist on mount
    wishlistStore.fetchWishlist();
  }, []);

  const handleMoveToCart = async (product: Product) => {
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }

    if (product.isSoldOut) {
      showToast(`${product.title} is out of stock`);
      return;
    }

    setShowLoader(true);
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }
      pageTrackingService.trackProductAddToCart(String(product.id), product.title || '', 'wishlist');
      if (product.quantity >= product.availableQuantity) {
        return;
      }
      wishlistStore.removeProductFromWishlist(product);
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setShowLoader(false);
    }
  };

  const handleRemoveItem = (product: Product) => {
    wishlistStore.removeProductFromWishlist(product);
    showToast('Removed from wishlist');
  };

  const soldOutItems = wishlist.filter(item => item.isSoldOut);
  const availableItems = wishlist.filter(item => !item.isSoldOut);

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader size={60} color="#2196F3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  const EmptyWishlist = () => {
    return (
      <>
        <Header />
        <div className={styles.emptyWishlistContainer}>
          <div className={styles.emptyWishlistContent}>
            <div className={styles.emptyWishlistIcon}>
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className={styles.emptyWishlistTitle}>Your Wishlist is Empty</h2>
            <p className={styles.emptyWishlistSubtitle}>
              Start adding your favorite games to your wishlist
            </p>
            <button
              type="button"
              className={styles.emptyWishlistButton}
              onClick={() => navigate('/')}
            >
              Browse Products
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  };

  if (wishlist.length === 0) {
    return <EmptyWishlist />;
  }

  return (
    <>
      {showLoader && (
        <div className={styles.loaderOverlay}>
          <Loader size={60} color="#2196F3" />
        </div>
      )}
      
      <Header />

      <div className={styles.wishlistPage}>
        <div className={styles.wishlistHeader}>
          <div className={styles.wishlistHeaderContent}>
            <h1 className={styles.wishlistTitle}>My Wishlist</h1>
            <p className={styles.wishlistCount}>{wishlist.length} {wishlist.length === 1 ? 'item' : 'items'}</p>
          </div>
        </div>

        <div className={styles.wishlistContent}>
          {/* Available Items */}
          {availableItems.length > 0 && (
            <div className={styles.wishlistSection}>
              <h2 className={styles.sectionTitle}>Available Items</h2>
              <div className={styles.wishlistList}>
                {availableItems.map((item) => (
                  <WishlistCard
                    key={item.id}
                    product={item}
                    onMoveToCart={handleMoveToCart}
                    onRemove={handleRemoveItem}
                    onNavigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sold Out Items */}
          {soldOutItems.length > 0 && (
            <div className={styles.wishlistSection}>
              <h2 className={styles.sectionTitle}>Out of Stock</h2>
              <div className={styles.wishlistList}>
                {soldOutItems.map((item) => (
                  <WishlistCard
                    key={item.id}
                    product={item}
                    onMoveToCart={handleMoveToCart}
                    onRemove={handleRemoveItem}
                    onNavigate={navigate}
                    isSoldOut={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
});

interface WishlistCardProps {
  product: Product;
  onMoveToCart: (product: Product) => void;
  onRemove: (product: Product) => void;
  onNavigate: (path: string) => void;
  isSoldOut?: boolean;
}

const WishlistCard: React.FC<WishlistCardProps> = ({
  product,
  onMoveToCart,
  onRemove,
  onNavigate,
  isSoldOut = false,
}) => {
  const defaultImage = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;

  return (
    <div className={`${styles.wishlistCard} ${isSoldOut ? styles.soldOut : ''}`}>
      <div className={styles.listItemContent}>
        <div
          className={styles.listItemImage}
          onClick={() => onNavigate(generateProductDetailsUrl(product))}
        >
          <img
            src={optimizeImageUrl(product.poster) || defaultImage}
            alt={product.title}
            loading="lazy"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.onerror = null;
              target.src = defaultImage;
            }}
          />
          {product.discountPercentage > 0 && (
            <div className={styles.discountBadge}>
              {formatDiscountPercentage(product.discountPercentage)}% OFF
            </div>
          )}
          {isSoldOut && (
            <div className={styles.soldOutBadge}>Out of Stock</div>
          )}
        </div>

        <div className={styles.listItemDetails}>
          <h3
            className={styles.listItemTitle}
            onClick={() => onNavigate(generateProductDetailsUrl(product))}
          >
            {product.title}
          </h3>

          <div className={styles.listItemMeta}>
            {product.offerFromStore && (
              <div className={styles.storeInfo}>
                <img
                  src={product.offerFromStore.icon || `${process.env.PUBLIC_URL}/images/icons/steam-icon.svg`}
                  alt={product.offerFromStore.text || 'Store'}
                  className={styles.storeIcon}
                />
                <span className={styles.storeName}>{product.offerFromStore.text}</span>
              </div>
            )}
            {product.availableRegion && (
              <span className={styles.region}>{product.availableRegion}</span>
            )}
          </div>

          <div className={styles.listItemPrice}>
            <span className={styles.currentPrice}>{product.buyPriceString}</span>
            {product.displayPrice > product.buyPrice && (
              <span className={styles.originalPrice}>{product.displayPriceString}</span>
            )}
          </div>

          <ItemMessage
            itemMessage={product.itemMessage}
            buyPrice={product.buyPrice}
            displayPrice={product.displayPrice}
            className={styles.itemMessage}
          />
        </div>

        <div className={styles.listItemActions}>
          {!isSoldOut && (
            <button
              className={styles.moveToCartButton}
              onClick={() => onMoveToCart(product)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V17C17 18.1 17.9 19 19 19C20.1 19 21 18.1 21 17V13M9 19.5C9.8 19.5 10.5 20.2 10.5 21C10.5 21.8 9.8 22.5 9 22.5C8.2 22.5 7.5 21.8 7.5 21C7.5 20.2 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21C21.5 21.8 20.8 22.5 20 22.5C19.2 22.5 18.5 21.8 18.5 21C18.5 20.2 19.2 19.5 20 19.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Move to Cart
            </button>
          )}
          <button
            className={styles.removeButton}
            onClick={() => onRemove(product)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

export default WishlistRedesign;

